/**
 * XFDF Utilities
 *
 * Single source of truth for all XFDF annotation string operations.
 *
 * Two XFDF formats are used in this app:
 *
 * **Standard format** (database / broadcast):
 * ```xml
 * <xfdf><annots><ink name="abc" .../></annots></xfdf>
 * ```
 *
 * **Command format** (WebViewer's `importAnnotationCommand`):
 * ```xml
 * <xfdf><add><ink name="abc" .../></add><modify/><delete/></xfdf>
 * ```
 */

// ============================================================================
// Constants
// ============================================================================

/** Empty XFDF document — used to suppress embedded PDF annotations in the viewer. */
export const EMPTY_XFDF =
  '<?xml version="1.0" encoding="UTF-8"?><xfdf xmlns="http://ns.adobe.com/xfdf/" xml:space="preserve"><annots/></xfdf>';

/**
 * Prefix/suffix for wrapping a single annotation element in a complete XFDF document.
 * Used when building per-annotation XFDF strings.
 */
export const XFDF_WRAPPER_START =
  '<?xml version="1.0" encoding="UTF-8"?><xfdf xmlns="http://ns.adobe.com/xfdf/" xml:space="preserve"><annots>';
export const XFDF_WRAPPER_END = "</annots></xfdf>";

/** A blank XFDF document skeleton for DOM-based construction. */
const XFDF_EMPTY_DOCUMENT =
  '<?xml version="1.0" encoding="UTF-8"?><xfdf xmlns="http://ns.adobe.com/xfdf/" xml:space="preserve"><annots></annots></xfdf>';

// ============================================================================
// Format detection
// ============================================================================

/**
 * Returns true if the XFDF string uses the standard `<annots>` format
 * (non-empty — at least one child element).
 */
export const isStandardXfdfFormat = (xfdf: string): boolean => {
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xfdf, "text/xml");
    if (xmlDoc.querySelector("parsererror")) return false;
    const annotsElement = xmlDoc.querySelector("annots");
    return annotsElement !== null && annotsElement.children.length > 0;
  } catch {
    return false;
  }
};

/**
 * Returns true if the XFDF string is in WebViewer's command format
 * (`<add>`, `<modify>`, or `<delete>` with at least one child).
 */
export const isCommandXfdfFormat = (xfdf: string): boolean => {
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xfdf, "text/xml");
    if (xmlDoc.querySelector("parsererror")) return false;
    const add = xmlDoc.querySelector("add");
    const modify = xmlDoc.querySelector("modify");
    const del = xmlDoc.querySelector("delete");
    return (
      (add !== null && add.children.length > 0) ||
      (modify !== null && modify.children.length > 0) ||
      (del !== null && del.children.length > 0)
    );
  } catch {
    return false;
  }
};

// ============================================================================
// ID extraction
// ============================================================================

/**
 * Extracts the `name` attribute of the **first** annotation element in a
 * standard-format XFDF string.
 *
 * Handles both `string` and `string | undefined` inputs.
 */
export const extractAnnotationId = (xfdf: string | undefined): string | null => {
  if (!xfdf) return null;
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xfdf, "text/xml");
    if (doc.querySelector("parsererror")) return null;
    const annotsElement = doc.querySelector("annots");
    if (!annotsElement?.firstElementChild) return null;
    return annotsElement.firstElementChild.getAttribute("name");
  } catch {
    return null;
  }
};

/**
 * Extracts the `name` attributes of **all** annotation elements in a
 * standard-format XFDF string.
 */
export const extractAllAnnotationIds = (xfdf: string | undefined): string[] => {
  if (!xfdf) return [];
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xfdf, "text/xml");
    if (doc.querySelector("parsererror")) return [];
    const annotsElement = doc.querySelector("annots");
    if (!annotsElement) return [];
    const ids: string[] = [];
    for (const child of annotsElement.children) {
      const id = child.getAttribute("name");
      if (id) ids.push(id);
    }
    return ids;
  } catch {
    return [];
  }
};

// ============================================================================
// Transformation
// ============================================================================

/**
 * Converts a standard-format XFDF string into WebViewer's command format
 * for use with `importAnnotationCommand()`.
 *
 * @param xfdf   Standard-format XFDF (`<annots>` wrapper)
 * @param action The command action (`"add"`, `"modify"`, or `"delete"`)
 * @returns Command-format XFDF string, or `null` on failure.
 */
export const wrapXfdfInCommand = (
  xfdf: string,
  action: "add" | "modify" | "delete",
): string | null => {
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xfdf, "text/xml");

    if (xmlDoc.querySelector("parsererror")) {
      console.error("[xfdf] Failed to parse XFDF for command wrapping");
      return null;
    }

    const annotsElement = xmlDoc.querySelector("annots");
    if (!annotsElement || annotsElement.children.length === 0) {
      console.error("[xfdf] No annotation elements found in <annots>");
      return null;
    }

    const serializer = new XMLSerializer();
    let annotStrings = "";
    for (const child of Array.from(annotsElement.children)) {
      annotStrings += serializer.serializeToString(child);
    }

    let commandXfdf = '<?xml version="1.0" encoding="UTF-8" ?>';
    commandXfdf += '<xfdf xmlns="http://ns.adobe.com/xfdf/" xml:space="preserve">';
    commandXfdf += "<fields />";

    if (action === "add") {
      commandXfdf += `<add>${annotStrings}</add><modify /><delete />`;
    } else if (action === "modify") {
      commandXfdf += `<add /><modify>${annotStrings}</modify><delete />`;
    } else {
      commandXfdf += `<add /><modify /><delete>${annotStrings}</delete>`;
    }

    commandXfdf += "</xfdf>";
    return commandXfdf;
  } catch (error) {
    console.error("[xfdf] Error wrapping XFDF in command:", error);
    return null;
  }
};

/**
 * Merges an array of per-annotation XFDF strings into a single XFDF document.
 *
 * Each item must have a `xfdf` string field (optional — items without XFDF are
 * silently skipped). The result is suitable for passing to WebViewer's
 * `importAnnotations()`.
 *
 * Returns `undefined` when no valid annotation elements are found.
 */
export const mergeAnnotationsToXfdf = (
  annotations: ReadonlyArray<{ xfdf?: string | null | undefined; id: string }>,
): string | undefined => {
  if (!annotations || annotations.length === 0) return undefined;

  try {
    const parser = new DOMParser();
    const serializer = new XMLSerializer();
    const allAnnotationNodes: Element[] = [];

    for (const annotation of annotations) {
      if (!annotation.xfdf) continue;

      try {
        const xmlDoc = parser.parseFromString(annotation.xfdf, "text/xml");
        if (xmlDoc.querySelector("parsererror")) {
          console.error("[xfdf] Failed to parse XFDF for annotation:", annotation.id);
          continue;
        }

        const annotsElement = xmlDoc.querySelector("annots");
        if (annotsElement && annotsElement.children.length > 0) {
          for (const child of Array.from(annotsElement.children)) {
            allAnnotationNodes.push(child);
          }
        }
      } catch (error) {
        console.error("[xfdf] Error parsing XFDF for annotation:", annotation.id, error);
      }
    }

    if (allAnnotationNodes.length === 0) return undefined;

    const mergedDoc = parser.parseFromString(XFDF_EMPTY_DOCUMENT, "text/xml");
    const annotsElement = mergedDoc.querySelector("annots");
    if (!annotsElement) {
      console.error("[xfdf] Failed to create merged XFDF structure");
      return undefined;
    }

    for (const node of allAnnotationNodes) {
      annotsElement.appendChild(mergedDoc.importNode(node, true));
    }

    return serializer.serializeToString(mergedDoc);
  } catch (error) {
    console.error("[xfdf] Error merging XFDF annotations:", error);
    return undefined;
  }
};

/**
 * Parses an XFDF string once and returns all annotation elements indexed by
 * their `name` attribute.
 *
 * For a single-element lookup, use `extractAnnotationElements(xfdf).get(name) ?? null`.
 *
 * Returns an empty `Map` when the XFDF is malformed or contains no elements.
 */
export const extractAnnotationElements = (xfdf: string): Map<string, Element> => {
  const result = new Map<string, Element>();
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xfdf, "text/xml");
    if (xmlDoc.querySelector("parsererror")) return result;
    const annotsElement = xmlDoc.querySelector("annots");
    if (!annotsElement) return result;
    for (const child of annotsElement.children) {
      const name = child.getAttribute("name");
      if (name) result.set(name, child);
    }
  } catch {
    // return empty map for unparseable XFDF
  }
  return result;
};

/**
 * Wraps a single annotation `Element` in a complete, well-formed XFDF document
 * string (standard format).
 *
 * Useful when the backend expects one XFDF string per annotation.
 */
export const buildSingleAnnotationXfdf = (element: Element): string => {
  const parser = new DOMParser();
  const serializer = new XMLSerializer();
  const doc = parser.parseFromString(XFDF_EMPTY_DOCUMENT, "text/xml");
  const annotsElement = doc.querySelector("annots");
  if (annotsElement) {
    annotsElement.appendChild(doc.importNode(element, true));
  }
  return serializer.serializeToString(doc);
};

// ============================================================================
// Annotation queries
// ============================================================================

/**
 * Returns true when the XFDF element represents a **status-only** change
 * (i.e. only `state` / `statemodel` attributes were modified, not actual
 * annotation content).
 */
export const isStateChangeOnly = (xfdfElement: Element): boolean => {
  if (!xfdfElement.hasAttribute("state")) return false;
  const nonStateAttributes = Array.from(xfdfElement.attributes).filter(
    (attr) => !["name", "page", "state", "statemodel"].includes(attr.name.toLowerCase()),
  );
  return nonStateAttributes.length === 0;
};

/**
 * Searches `annotations` for the entry whose XFDF `<annots>` element has a
 * child with `name === annotationName`.
 *
 * Works with any array of objects that have an optional `xfdf` field — no
 * domain-specific `Annotation` type dependency.
 */
export const findAnnotationByName = <T extends { xfdf?: string | null | undefined }>(
  annotations: T[] | undefined,
  annotationName: string,
): T | undefined => {
  if (!annotations) return undefined;

  const parser = new DOMParser();

  for (const annotation of annotations) {
    if (!annotation.xfdf) continue;

    try {
      const xmlDoc = parser.parseFromString(annotation.xfdf, "text/xml");
      if (xmlDoc.querySelector("parsererror")) continue;

      const annotsElement = xmlDoc.querySelector("annots");
      if (!annotsElement) continue;

      const found = Array.from(annotsElement.children).some(
        (child) => child.getAttribute("name") === annotationName,
      );
      if (found) return annotation;
    } catch {
      // silently skip unparseable XFDF
    }
  }

  return undefined;
};

// ============================================================================
// File attachment helpers
// ============================================================================

/**
 * Returns the byte size of the embedded file data inside a `<fileattachment>`
 * XFDF element, or `null` if the XFDF does not contain a file attachment.
 *
 * The value comes from the `length` attribute on the `<data>` child element,
 * which represents the compressed (FlateDecode) byte count — i.e. the size
 * stored and transmitted.
 */
export const getFileAttachmentSizeBytes = (xfdf: string): number | null => {
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xfdf, "text/xml");
    if (xmlDoc.querySelector("parsererror")) return null;
    const fileAttachment = xmlDoc.querySelector("fileattachment");
    if (!fileAttachment) return null;
    const dataElement = fileAttachment.querySelector("data");
    if (!dataElement) return null;
    const lengthAttr = dataElement.getAttribute("length");
    if (!lengthAttr) return null;
    const size = Number.parseInt(lengthAttr, 10);
    if (Number.isNaN(size)) return null;
    return size;
  } catch {
    return null;
  }
};
